import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ConfigureExperience from "./ConfigureExperience";
import { getTemplateById, editExperience, getExperienceById, createExperience } from '../../utils/APIs';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';
import { useUserContext } from '../../contexts/UserContext';
import ErrorAlert from '../UI/Alert/ErrorAlert';
import Loader from '../UI/Loader/Loader';
import SuccessAlert from '../UI/Alert/SuccessAlert';
import ExperienceCode from '../ExperienceCode';
import { permission } from '../../config/permission';
import { addExperienceIdToHTML, getObjFromHtml, isValidJsonString, transformExperienceJSON, updateHtmlTemplate } from '../../utils/helper';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import Activation from '../ExperienceCode/Activation';
import { toast } from 'react-toastify';
import Button from '../UI/Button';
import Input from '../UI/Input';
import { usePreviewMode } from '../../contexts/PreviewModeContext';
import { ChevronDown, Check, ChevronRight } from "lucide-react";
import ChooseTemplate from "../Template/ChooseTemplate";
import PublishExperienceModal from "./PublishExperienceModal";
import { createPortal } from "react-dom";
import { resolveUploadFields, hasUploadFields } from '../../utils/uploadHelper';

const EditExperience = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate()
  const { enums, content } = useContentfulContext();
  const { enablePreviewMode, disablePreviewMode, showNavbar, showSidebar } = usePreviewMode();

  const [experienceName, setExperienceName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [template, setTemplate] = useState({});
  const [experience, setExperience] = useState();
  // const { experiencesData } = useContext(ExperienceContext);
  const { userData, checkPermission } = useUserContext();
  const [fieldValues, setFieldValues] = useState();
  const [hasChanges, setHasChanges] = useState(false);
  const [transformedExperienceJSON, setTransformedExperienceJSON] = useState();
  const [currentStep, setCurrentStep] = useState('configure-experience');
  const [, setOfferId] = useState();
  const [validationError, setValidationError] = useState("");
  const [dynamicFields, setDynamicFields] = useState({})
  const [arrayFields, setArrayFields] = useState({})
  const [code, setCode] = useState();
  const [type, setType] = useState("");
  const [isOpen, setIsOpen] = useState(false)

  const [menuPosition, setMenuPosition] = useState(null);

  // Upload field state
  const [pendingUploads, setPendingUploads] = useState(new Map());
  const [isUploading, setIsUploading] = useState(false);

  // New state for version management
  const [currentVersion, setCurrentVersion] = useState(null);
  const [isPublishedVersion, setIsPublishedVersion] = useState(false);
  const [publishModalState, setPublishModalState] = useState({
    isOpen: false,
    action: '', // 'publish', 'saveAndClose', 'saveAndActivate'
  });

  const getExperienceByIdHandler = async (id) => {
    try {
      setLoading(true);
      const experienceData = await getExperienceById(
        userData?.token,
        userData?.selectedOrganization?.org_token,
        id,
        userData?.defaultAdobeIO ? userData?.defaultAdobeIO?._id : null,
        userData?.defaultContentful ? userData?.defaultContentful._id : null,
        userData?.defaultTealiumKey ? userData?.defaultTealiumKey?._id : null,
        userData?.defaultGithub ? userData?.defaultGithub?._id : null,
      );

      // Determine correct version to use
      const versionFromState = location.state?.version;
      const currentVersionNo = versionFromState || experienceData.data?.published_version;
      setCurrentVersion(currentVersionNo);
      setIsPublishedVersion(currentVersionNo === experienceData.data?.published_version);

      // Loop through versions array to get the effective experience JSON for the selected version
      let effectiveJson = experienceData.data.experience_json; // fallback in case versions are missing
      if (Array.isArray(experienceData.data.versions) && experienceData.data.versions.length > 0) {
        const targetVersion = experienceData.data.versions.find(v => v.version_no === currentVersionNo);
        if (targetVersion) {
          effectiveJson = targetVersion.experience_json;
        }
      }
      if (experienceData.data.templates.length === 0) {
        const findType = isValidJsonString(effectiveJson) ? "json" : "content"
        setCurrentStep("choose-template")
        setErrorMessage('');
        setExperience({ ...experienceData.data, experience_json: effectiveJson })
        setType(findType)
      } else {
        const templateData = await getTemplateById(
          userData?.token,
          userData?.selectedOrganization?.org_token,
          experienceData.data.templates[0]._id,
          userData?.defaultAEMAdobeIO?._id,
        );

        if (experienceData.data.templates[0].type === enums?.templateTypes.json_a_to_html.id) {
          const parsedJsonSchema = JSON.parse(templateData?.data?.json_schema)

          const updatedExperienceJson = getObjFromHtml(effectiveJson, parsedJsonSchema.config_obj_key)
          setExperience({ ...experienceData.data, experience_json: JSON.stringify(updatedExperienceJson) })
        } else {
          setExperience({ ...experienceData.data, experience_json: effectiveJson })
        }

        setTemplate(templateData.data);
        const filtered = JSON.parse(templateData.data.config_json).filter((x) => x.input.input_type === "array")
        const arrToObj = filtered.reduce((acc, curr) => {
          acc[curr.id] = curr;
          return acc;
        }, {});
        setArrayFields(arrToObj);
        setErrorMessage('');
      }
    } catch (error) {
      console.log('error', error)
      setErrorMessage(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
    } finally {
      setLoading(false);
    }
  };

  const editExperienceHandler = async (saveAction = 'update', callback) => {
    try {
      if (validationError) return;

      const isValidate = handleValidateJson();
      if (isValidate.length > 0) return;

      setLoading(true);

      // fieldValues already have CDN URLs (resolved on Next click)
      const updatehtml = updateHtmlTemplate(template.html, fieldValues)

      const payload = {
        name: experienceName,
        templates: [template._id],
        html: '',
        css: '',
        config_json: '',
        experience_json: template.type === enums?.templateTypes.json_a_to_html.id ? updatehtml : JSON.stringify(transformExperienceJSON(fieldValues, dynamicFields)),
      };

      // Save which Contentful integration was used
      if (hasUploadFields(template.config_json, 'contentful') && userData?.defaultContentful?._id) {
        payload.upload_contentful_integration_id = userData?.defaultContentful?._id;
      }

      if (saveAction === "update") {
        payload.version = currentVersion
      } else if (saveAction === "publish") {
        payload.version = currentVersion
        payload.do_publish = true
      }
      const result = await editExperience(
        userData?.token,
        userData?.selectedOrganization?.org_token,
        experience._id,
        userData?.defaultAdobeIO ? userData?.defaultAdobeIO?._id : null,
        userData?.defaultContentful ? userData?.defaultContentful._id : null,
        userData?.defaultTealiumKey ? userData?.defaultTealiumKey?._id : null,
        payload,
        {
          event_name: ANALYTICS_EVENTS.UPDATE_EXPERIENCE,
          metadata: { experience_id: experience._id, experience_name: experienceName, save_action: saveAction, source: 'stride', approved: saveAction === "publish" }
        }
      );
      if (template && (template?.type === enums?.templateTypes.json_a_to_html.id)) {
        const parsedJsonSchema = JSON.parse(template?.json_schema)
        const updatedExperienceJson = getObjFromHtml(result?.data?.experience_json, parsedJsonSchema?.config_obj_key)
        setExperience({ ...result?.data, experience_json: JSON.stringify(updatedExperienceJson) })

        const versionNo = result?.data?.versions[result?.data.versions.length - 1].version_no
        setCode(addExperienceIdToHTML(code, id, versionNo))
      } else {
        setExperience(result.data)
      }

      setExperienceName(result.data.name);
      setErrorMessage('');

      // Update success message based on action)
      let successMsg = '';
      switch (saveAction) {
        case 'update':
          successMsg = isPublishedVersion ? content.EDIT_EXPERIENCE.success_updated : content.EDIT_EXPERIENCE.success_draft_saved;
          break;
        case 'publish':
          successMsg = content.EDIT_EXPERIENCE.success_published;
          break;
        case 'new_version':
          successMsg = content.EDIT_EXPERIENCE.success_new_version;
          break;
        default:
          successMsg = content.EDIT_EXPERIENCE.success;
      }

      toast.success(successMsg);

      // Update version state after successful save
      if (saveAction === 'new_version') {
        const newVersionNo = result.data.versions[result.data.versions.length - 1]?.version_no;
        setCurrentVersion(newVersionNo);
        setIsPublishedVersion(false); // New versions are always draft
      } else if (saveAction === 'publish') {
        setIsPublishedVersion(true);
      }

      if (checkPermission(permission.EDIT_EXP_OP_ACTIVATE)) {
        setCurrentStep("activation")
      } else {
        navigate("/experience/manage-experiences");
      }

      if (typeof callback === "function") {
        callback()
      }
    } catch (error) {
      setErrorMessage(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
    } finally {
      setLoading(false);
    }
  };

  const createNewExperience = async (newName) => {
    try {
      if (!newName?.trim()) {
        setErrorMessage(content.EDIT_EXPERIENCE.exp_name_required);
        return;
      }

      setLoading(true);

      const updatedHtml = updateHtmlTemplate(experience.templates?.[0]?.html, fieldValues);

      const payload = {
        name: newName,
        templates: [experience.templates?.[0]?._id],
        html: '',
        css: '',
        config_json: '',
        experience_json: experience.templates?.[0]?.type === enums.templateTypes.json_a_to_html.id
          ? updatedHtml
          : JSON.stringify(transformExperienceJSON(fieldValues, dynamicFields)),
        source: enums.experience_sources.stride.id,
      };

      const result = await createExperience(
        userData?.token,
        userData?.selectedOrganization?.org_token,
        payload
      );
      if (result.status) {
        toast.success(result.message || "New experience created");
        setPublishModalState({ isOpen: false, action: 'publish' });
        navigate("/experience/manage-experiences")
      }

      // Redirect to edit page of new experience
    } catch (error) {
      setErrorMessage(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
    } finally {
      setLoading(false);
    }
  };

  const validateExperienceName = () => {
    if (!experienceName.trim()) {
      setErrorMessage(content.EDIT_EXPERIENCE.exp_name_required);
      return false;
    }
    setErrorMessage("");
    return true;
  };

  function getFlattenExperienceJSON(ob) {
    let result = {};
    function recurse(cur, prop) {
      if (Object(cur) !== cur) {
        result[prop] = cur;
      } else if (Array.isArray(cur)) {
        if (arrayFields[prop]) {
          result[prop] = cur;
        } else {
          for (let i = 0; i < cur.length; i++) {
            recurse(cur[i], prop ? prop + "[" + i + "]" : i);
          }
        }
        if (cur.length === 0) result[prop] = [];
      } else {
        let isEmpty = true;
        for (let p in cur) {
          isEmpty = false;
          recurse(cur[p], prop ? prop + "." + p : p);
        }
        if (isEmpty && prop) result[prop] = {};
      }
    }
    recurse(ob, "");
    return result;
  }

  useEffect(() => {
    if (!checkPermission(permission.EDIT_EXP_OP_CONFIGURE_EXPERIENCE)) {
      setCurrentStep("")
    }
    getExperienceByIdHandler(id);
  }, []);

  useEffect(() => {
    if (Object.keys(template).length > 0) {
      setFieldValues(() => {
        const base_json = {};
        console.log("json parse template")
        JSON.parse(template.config_json).forEach(function (item, index) {
          const key = item.exp_json_field;
          base_json[key] = item.input.input_default_value;
        })
        return base_json;
      });
    }
    if (currentStep === 'choose-template' && template._id) {
      setCurrentStep('configure-experience');
    }

    if (currentStep === 'choose-template' && template._id) {
      setCurrentStep('configure-experience');
    }
  }, [template]);


  /**
   * handleNextFromConfigure
   * Called when Next is clicked on configure-experience step.
   * Uploads pending files so the code view shows CDN URLs immediately.
   */
  const handleNextFromConfigure = async () => {
    if (pendingUploads.size > 0) {
      try {
        setIsUploading(true);
        const resolvedFieldValues = await resolveUploadFields(
          fieldValues,
          pendingUploads,
          template.config_json,
          userData?.defaultContentful?._id,
          userData?.token,
          userData?.selectedOrganization?.org_token
        );
        setFieldValues(resolvedFieldValues);
        setPendingUploads(new Map());
      } catch (err) {
        setErrorMessage(err?.message || 'Failed to upload asset. Please try again.');
        setIsUploading(false);
        return;
      } finally {
        setIsUploading(false);
      }
    }
    setCurrentStep('create-offer');
  };

  useEffect(() => {
    if (experience) {
      setExperienceName(experience.name);

      let flatExperienceJson;

      if (template?.type === enums?.templateTypes.json_a_to_html.id) {

        if (experience.templates.length > 0) {
          flatExperienceJson = JSON.parse(experience.experience_json);
        } else {
          // Extract object from HTML
          const parsedJsonSchema = JSON.parse(template?.config_json);
          flatExperienceJson = getObjFromHtml(experience.experience_json, parsedJsonSchema.config_obj_key);
        }
      } else {
        try {
          flatExperienceJson = JSON.parse(experience.experience_json);
        } catch (err) {
          console.error("Failed to parse experience_json:", err, experience.experience_json);
          flatExperienceJson = {};
        }
      }

      let flattenJSON = getFlattenExperienceJSON(flatExperienceJson);
      let temps = {};

      for (const key in flattenJSON) {
        if (flattenJSON.hasOwnProperty(key)) {
          if (key.includes('.0.')) {
            flattenJSON[key.replace('.0.', '[0].')] = flattenJSON[key];
            delete flattenJSON[key];
          } else if (key.includes('.1.')) {
            flattenJSON[key.replace('.1.', '[1].')] = flattenJSON[key];
            delete flattenJSON[key];
          } else if (key.includes('.2.')) {
            flattenJSON[key.replace('.2.', '[2].')] = flattenJSON[key];
            delete flattenJSON[key];
          }
        }

        let element;
        if (template?.config_json) {
          element = JSON.parse(template.config_json).find((x) => x.id === key);
        }

        if (element?.input?.input_type === "dynamic-text") {
          temps[element.id] = flattenJSON[key];
          flattenJSON[key] = element.input?.input_default_value;
        }
      }

      setDynamicFields(temps);
      setFieldValues(flattenJSON);
    }
  }, [experience]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setErrorMessage('');
      setSuccessMessage('');
    }, 4000);

    return () => clearTimeout(timer);
  }, [errorMessage, successMessage]);

  useEffect(() => {
    if (Object.keys(template).length > 0) {
      if (JSON.stringify(fieldValues) == experience.experience_json &&
        experienceName == experience.name) {
        setHasChanges(false);
      } else {
        setHasChanges(true);
      }
    }
  }, [fieldValues, experienceName]);

  const handleSave = () => {
    setValidationError("")
    if (currentStep === 'choose-template') {
      setCurrentStep('configure-experience');
    } else if (currentStep == 'configure-experience') {
      if (!checkPermission(permission.EDIT_EXP_OP_EDIT_EXPERIENCE)) {
        return;
      }
      setCurrentStep('create-offer');
    } else if (currentStep == 'create-offer') {
      if (!validateExperienceName()) return;
      if (!errorMessage && hasChanges) {
        editExperienceHandler('update');
      }
    }
  }

  const handleValidateJson = () => {
    setValidationError("");
    const config_elems = [];
    JSON.parse(template.config_json).forEach((el) => {
      el.error = "";
      config_elems.push({ ...el, id: el.id });
      // uncomment below line and remove above line if edit experience is not working
      // config_elems.push({ ...el, id: el.id.replace("[", ".").replace("]", "") });
    });
    const newConfigElems = [...config_elems];
    const errs = [];

    for (let item of newConfigElems) {
      let val = "";
      if (dynamicFields[item.id] !== undefined) {
        val = dynamicFields[item.id]
      } else {
        val = fieldValues[item.id];
      }

      const rules = item.rules;
      if (rules.is_mandatory) {
        // For upload fields: a pending file counts as a value
        const hasPendingFile = item.input?.input_type === 'upload' && pendingUploads.has(item.exp_json_field || item.id);
        if (!hasPendingFile && val?.length == 0) {
          const message = "Field is required";
          item.error = message;
          errs.push(`${item.id}: ${message}`)
        } else {
          item.error = "";
        }
      }
      if (rules.min_length && rules.max_length && item.error.length == 0) {
        if (val.length > rules.max_length || val.length < rules.min_length) {
          const message = `Min length: ${rules.min_length} and Max length: ${rules.max_length}`;
          item.error = message;
          errs.push(`${item.id}: ${message}`);
        } else {
          item.error = "";
        }
      }
    }

    if (errs.length > 0) {
      setValidationError(errs.join(", \n"));
    }
    return errs;
  }

  useEffect(() => {
    if (currentStep == "configure-experience") {
      enablePreviewMode();
    } else {
      disablePreviewMode();
    }
  }, [currentStep])

  return (
    <div>
      {loading && <Loader />}
      {errorMessage && <ErrorAlert timeout={4000}>{errorMessage}</ErrorAlert>}
      {successMessage && <SuccessAlert disappearTime={4000}>{successMessage}</SuccessAlert>}

      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-border shadow-xs px-4 md:px-8 py-3 mb-4 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
        <div className="w-full md:max-w-[260px]">
          <Input
            size="sm"
            disabled={currentStep === 'activation'}
            id="experience_name"
            name="experience_name"
            placeholder={content.EDIT_EXPERIENCE.exp_name}
            value={experienceName}
            onChange={(e) => {
              if (currentStep === 'activation') return;
              setExperienceName(e.target.value);
            }}
          />
        </div>

        <nav aria-label="Progress" className="flex items-center justify-center flex-1 gap-1.5 md:gap-2 flex-wrap">
          {experience?.templates && experience.templates.length === 0 && (
            <>
              <button
                type="button"
                onClick={() => setCurrentStep('choose-template')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-colors ${
                  currentStep === 'choose-template'
                    ? 'bg-primary text-white font-semibold shadow-xs'
                    : 'bg-emerald-50 text-emerald-700 font-medium hover:bg-emerald-100 border border-emerald-200/60'
                }`}
              >
                {currentStep !== 'choose-template' ? (
                  <Check className="w-3.5 h-3.5" />
                ) : (
                  <span className="w-4 h-4 rounded-full bg-white/20 text-white flex items-center justify-center text-[10px] font-bold">1</span>
                )}
                <span>Choose Template</span>
              </button>
              <ChevronRight className="w-3.5 h-3.5 text-stride-muted-light hidden sm:inline-block" />
            </>
          )}

          {checkPermission(permission.EDIT_EXP_OP_CONFIGURE_EXPERIENCE) && (
            <button
              type="button"
              disabled={!template}
              onClick={() => {
                if (template) {
                  setCurrentStep('configure-experience');
                }
              }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                currentStep === 'configure-experience'
                  ? 'bg-primary text-white font-semibold shadow-xs'
                  : (currentStep === 'create-offer' || currentStep === 'activation')
                    ? 'bg-emerald-50 text-emerald-700 font-medium hover:bg-emerald-100 border border-emerald-200/60'
                    : 'bg-surface-alt text-stride-muted font-medium border border-border-subtle'
              }`}
            >
              {(currentStep === 'create-offer' || currentStep === 'activation') ? (
                <Check className="w-3.5 h-3.5" />
              ) : (
                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${currentStep === 'configure-experience' ? 'bg-white/20 text-white' : 'bg-border text-stride-muted'}`}>1</span>
              )}
              <span>{content.EDIT_EXPERIENCE.config_exp}</span>
            </button>
          )}

          <ChevronRight className="w-3.5 h-3.5 text-stride-muted-light hidden sm:inline-block" />

          {checkPermission(permission.EDIT_EXP_OP_EDIT_EXPERIENCE) && (
            <button
              type="button"
              disabled={!template}
              onClick={() => {
                if (template) {
                  setCurrentStep('create-offer');
                }
              }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                currentStep === 'create-offer'
                  ? 'bg-primary text-white font-semibold shadow-xs'
                  : currentStep === 'activation'
                    ? 'bg-emerald-50 text-emerald-700 font-medium hover:bg-emerald-100 border border-emerald-200/60'
                    : 'bg-surface-alt text-stride-muted font-medium border border-border-subtle'
              }`}
            >
              {currentStep === 'activation' ? (
                <Check className="w-3.5 h-3.5" />
              ) : (
                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${currentStep === 'create-offer' ? 'bg-white/20 text-white' : 'bg-border text-stride-muted'}`}>2</span>
              )}
              <span>{content.EDIT_EXPERIENCE.edit_exp}</span>
            </button>
          )}

          <ChevronRight className="w-3.5 h-3.5 text-stride-muted-light hidden sm:inline-block" />

          {checkPermission(permission.EDIT_EXP_OP_ACTIVATE) && (
            <button
              type="button"
              disabled={!template}
              onClick={() => {
                if (template) {
                  setCurrentStep('activation');
                }
              }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                currentStep === 'activation'
                  ? 'bg-primary text-white font-semibold shadow-xs'
                  : 'bg-surface-alt text-stride-muted font-medium border border-border-subtle'
              }`}
            >
              <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${currentStep === 'activation' ? 'bg-white/20 text-white' : 'bg-border text-stride-muted'}`}>3</span>
              <span>{content.EDIT_EXPERIENCE.activate_exp}</span>
            </button>
          )}
        </nav>

        <div className="w-full md:w-auto flex justify-end items-center gap-2.5">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              if (currentStep === "activation") {
                navigate(`/experience/experience-details/${id}`);
              } else {
                navigate("/experience/manage-experiences");
              }
            }}
          >
            {content.EDIT_EXPERIENCE.cancel_btn}
          </Button>

          {currentStep !== "activation" && (
            <div className="flex items-center">
              <Button
                id={currentStep == "create-offer" ? "save-as-experience-btn-id" : "go-to-next-step-btn-id"}
                variant="primary"
                size="sm"
                disabled={isUploading || loading}
                className={currentStep == "create-offer" && checkPermission(permission.EDIT_EXP_OP_ACTIVATE) ? "rounded-r-none" : ""}
                onClick={() => {
                  setValidationError("");
                  if (currentStep == "configure-experience") {
                    if (!checkPermission(permission.EDIT_EXP_OP_EDIT_EXPERIENCE)) {
                      return;
                    }
                    handleNextFromConfigure();
                  } else if (currentStep === "create-offer") {
                    if (!validateExperienceName()) return;
                    if (!errorMessage && hasChanges) {
                      if (isPublishedVersion) {
                        setPublishModalState({ isOpen: true, action: 'saveAndActivate' });
                      } else {
                        editExperienceHandler('update', () => {
                          navigate("/experience/manage-experiences");
                        });
                      }
                    }
                  }
                }}
              >
                {isUploading ? (
                  <span className="flex items-center gap-1.5">
                    <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Uploading…
                  </span>
                ) : currentStep === "create-offer" ? (
                  checkPermission(permission.EDIT_EXP_OP_ACTIVATE) ? (
                    isPublishedVersion
                      ? content.EDIT_EXPERIENCE.save_and_activate || "Save and Activate"
                      : content.EDIT_EXPERIENCE.save_and_close || "Save and Close"
                  ) : (
                    content.EDIT_EXPERIENCE.save_and_close || "Save and Close"
                  )
                ) : (
                  content.EDIT_EXPERIENCE.next_btn || "Next"
                )}
              </Button>
              {currentStep === "create-offer" && checkPermission(permission.EDIT_EXP_OP_ACTIVATE) && (
                <div className="relative">
                  <button
                    type="button"
                    data-testid="edit-exp-down-arrow"
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setMenuPosition({
                        top: rect.bottom + window.scrollY,
                        left: rect.right + window.scrollX,
                        width: rect.width
                      });
                      setIsOpen((prev) => !prev);
                    }}
                    className="h-8 px-2 bg-primary hover:bg-primary-hover text-white rounded-r-lg border-l border-primary-hover flex items-center justify-center transition-colors"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  {isOpen && menuPosition &&
                    createPortal(
                      <div
                        style={{
                          position: "absolute",
                          top: menuPosition.top,
                          left: menuPosition.left - 192,
                          width: 192,
                          zIndex: 9999
                        }}
                        className="bg-white shadow-dropdown rounded-lg border border-border py-1"
                      >
                        <div className="py-1">
                          {isPublishedVersion && (
                            <button
                              type="button"
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              onClick={() => {
                                setIsOpen(false);
                                setPublishModalState({ isOpen: true, action: "saveAndClose" });
                              }}
                            >
                              {content.EDIT_EXPERIENCE.save_and_close || "Save and Close"}
                            </button>
                          )}

                          {!isPublishedVersion && (
                            <button
                              type="button"
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              onClick={() => {
                                setIsOpen(false);
                                setPublishModalState({ isOpen: true, action: "publish" });
                              }}
                            >
                              {content.EDIT_EXPERIENCE.save_and_publish || "Save and Publish"}
                            </button>
                          )}

                          <button
                            type="button"
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => {
                              setIsOpen(false);
                              editExperienceHandler("new_version");
                            }}
                          >
                            {content.EDIT_EXPERIENCE.save_with_new_version || "Save with New Version"}
                          </button>
                        </div>
                      </div>,
                      document.body
                    )
                  }
                </div>
              )}
            </div>
          )}
        </div>
      </header>
      <div className='h-full px-4 md:px-8'>
        {currentStep == 'choose-template' &&
          <ChooseTemplate
            setCurrentStep={setCurrentStep}
            selectedTemplate={template}
            setSelectedTemplate={setTemplate}
            type={type}
            offerJson={{ content: type === "json" ? JSON.parse(experience?.experience_json) : experience?.experience_json }}
          />}
        {currentStep == 'configure-experience' && fieldValues && Object.keys(template).length > 0 &&
          <ConfigureExperience
            actionText={"Edit Experience"}
            selectedTemplate={template}
            fieldValues={fieldValues}
            setFieldValues={setFieldValues}
            transformedExperienceJSON={transformedExperienceJSON}
            setTransformedExperienceJSON={setTransformedExperienceJSON}
            dynamicFields={dynamicFields}
            setDynamicFields={setDynamicFields}
            pendingUploads={pendingUploads}
            setPendingUploads={setPendingUploads}
          />}
        {currentStep == 'create-offer' &&
          <ExperienceCode
            code={code}
            setCode={setCode}
            experience={experience}
            selectedTemplate={template}
            experienceJSON={transformedExperienceJSON}
            permissionId={permission.EDIT_EXP_OP_EDIT_EXP_CODE}
            setFieldValues={setFieldValues}
            validationError={validationError}
            setValidationError={setValidationError}
            fieldValues={fieldValues}
            dynamicFields={dynamicFields}
            setDynamicFields={setDynamicFields}
          />}
        {currentStep == 'activation' &&
          <Activation
            code={code}
            experience={experience}
            setExperience={setExperience}
            selectedTemplate={template}
            setOfferId={setOfferId}
            setValidationError={setValidationError}
            validationError={validationError}
            handleValidateJson={handleValidateJson}
            offerPermission={{
              create: permission.CREATE_EXP_OP_CREATE_OFFER,
              edit: permission.CREATE_EXP_OP_EDIT_OFFER
            }}
            contentfulPermission={{
              create: permission?.EDIT_EXP_OP_CREATE_CONTENTFUL,
              edit: permission?.EDIT_EXP_OP_UPDATE_CONTENTFUL
            }}
            tealiumPermission={{
              create: permission.CREATE_EXP_OP_CREATE_TEALIUM,
              edit: permission.CREATE_EXP_OP_UPDATE_TEALIUM
            }}
          />
        }
      </div>
      {publishModalState.isOpen && (
        <PublishExperienceModal
          isOpen={publishModalState.isOpen}
          loading={loading}
          onClose={() => setPublishModalState({ ...publishModalState, isOpen: false })}
          experience={experience}
          onCancelDraft={() => {
            setPublishModalState({ ...publishModalState, isOpen: false });
            // if (publishModalState.action === 'publish' && !errorMessage && hasChanges) {
            //   editExperienceHandler('update', () => navigate(`/experience/experience-details/${experience._id}`));
            // }
          }}
          onCreateNewExperience={(newName) => {
            createNewExperience(newName);
          }}
          onPublish={() => {
            setValidationError("");
            if (!validateExperienceName()) return;

            if (!errorMessage && hasChanges) {
              switch (publishModalState.action) {
                case 'publish':
                  editExperienceHandler('publish', () => {
                    setPublishModalState({ ...publishModalState, isOpen: false });
                    if (!checkPermission(permission.EDIT_EXP_OP_ACTIVATE)) {
                      navigate("/experience/manage-experiences");
                    }
                  });
                  break;
                case 'saveAndClose':
                  editExperienceHandler('update', () => {
                    setPublishModalState({ ...publishModalState, isOpen: false });
                    navigate(`/experience/experience-details/${experience._id}`);
                  });
                  break;
                case 'saveAndActivate':
                  editExperienceHandler('update', () => {
                    setPublishModalState({ ...publishModalState, isOpen: false });
                    if (isPublishedVersion) {
                      if (checkPermission(permission.EDIT_EXP_OP_ACTIVATE)) {
                        // go to activation step
                        setCurrentStep('activation');
                      } else {
                        // cannot activate → just navigate to experience details
                        navigate(`/experience/experience-details/${experience._id}`);
                      }
                    } else {
                      // draft → just save and navigate back
                      navigate("/experience/manage-experiences");
                    }
                  });
                  break;
                default:
                  console.warn('Unknown publishModalState.action:', publishModalState.action);
                  break;
              }
            }
          }}
        />
      )}
    </div>
  )
}

export default EditExperience;